import { Icon } from "@elit-hair/elit-ui";
import { useTranslation } from "react-i18next";
import { TbCheck, TbListCheck } from "react-icons/tb";

import { useCalendarCapacitiesOptions } from "@/hooks/queries/calendar-capacities.queries";

import { useCalendarCapacitiesSelection } from "../hooks/use-calendar-capacities-selection";
import {
    CalendarCapacitiesSelectorVariant,
    type CalendarCapacitiesVisibleConfig,
    type CalendarCapacityOption,
    isAllCalendarCapacitiesSelected,
    isCalendarCapacitySelected,
} from "../utils/calendar-capacities.utils";

type CalendarCapacitiesSelectorBaseProps = {
    /** Calendars available for toggling; when omitted, derived from calendar options hook. */
    options?: CalendarCapacityOption[];
    variant?: CalendarCapacitiesSelectorVariant;
};

type CalendarCapacitiesSelectorControlledProps = CalendarCapacitiesSelectorBaseProps & {
    selectedCalendars: CalendarCapacitiesVisibleConfig;
    onToggle: (name: string) => void;
    onToggleAll: () => void;
};

type CalendarCapacitiesSelectorUncontrolledProps = CalendarCapacitiesSelectorBaseProps & {
    selectedCalendars?: never;
    onToggle?: never;
    onToggleAll?: never;
};

type Props = CalendarCapacitiesSelectorControlledProps | CalendarCapacitiesSelectorUncontrolledProps;

type SelectorContentProps = {
    options: CalendarCapacityOption[];
    selectedCalendars: string[];
    toggleCalendar: (name: string) => void;
    toggleAllCalendars: () => void;
    variant: CalendarCapacitiesSelectorVariant;
};

function CalendarCapacitySelectAllButton({
    isSelected,
    onClick,
    isInline,
    label,
}: {
    isSelected: boolean;
    onClick: () => void;
    isInline: boolean;
    label: string;
}) {
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            aria-pressed={isSelected}
            className={
                isInline
                    ? `inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                          isSelected
                              ? "border-primary bg-primary text-primary-foreground shadow-sm"
                              : "border-dashed border-border bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`
                    : `flex w-full items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                          isSelected
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-dashed border-border bg-muted/30 text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`
            }
        >
            <Icon icon={TbListCheck} size={isInline ? 14 : 16} className="shrink-0" aria-hidden />
            <span>{label}</span>
        </button>
    );
}

function CalendarCapacitiesSelectorContent({
    options,
    selectedCalendars,
    toggleCalendar,
    toggleAllCalendars,
    variant,
}: SelectorContentProps) {
    const { t } = useTranslation();
    const isInline = variant === CalendarCapacitiesSelectorVariant.INLINE;
    const allCalendarNames = options.map((option) => option.name);
    const isAllSelected = isAllCalendarCapacitiesSelected(selectedCalendars, allCalendarNames);

    return (
        <div className={isInline ? "flex flex-col gap-1.5" : undefined}>
            <p
                className={
                    isInline
                        ? "text-xs font-medium text-muted-foreground"
                        : "text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 py-1 mb-1"
                }
            >
                {t("calendars")}
            </p>
            {options.length === 0 ? (
                <p className="text-xs text-muted-foreground px-2">{t("calendar_capacities_no_calendars_available")}</p>
            ) : (
                <div className={isInline ? "flex flex-col gap-2" : undefined}>
                    <div className={isInline ? undefined : "px-2 pb-2 border-b border-border/60"}>
                        <CalendarCapacitySelectAllButton
                            isSelected={isAllSelected}
                            label={t("all")}
                            onClick={toggleAllCalendars}
                            isInline={isInline}
                        />
                    </div>
                    <div className={isInline ? "flex flex-wrap gap-2" : "max-h-64 overflow-y-auto pt-1"}>
                        {options.map((calendar) => {
                            const isSelected = isCalendarCapacitySelected(selectedCalendars, calendar.name);
                            return (
                                <button
                                    key={calendar.name}
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleCalendar(calendar.name);
                                    }}
                                    className={
                                        isInline
                                            ? "flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-muted transition-colors"
                                            : "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted transition-colors"
                                    }
                                >
                                    <div
                                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                                            isSelected ? "bg-primary border-primary" : "border-border"
                                        }`}
                                    >
                                        {isSelected && (
                                            <Icon icon={TbCheck} size={11} className="text-primary-foreground" />
                                        )}
                                    </div>
                                    {calendar.color ? (
                                        <span
                                            className="h-3 w-1 shrink-0 rounded-full"
                                            style={{ backgroundColor: calendar.color }}
                                            aria-hidden
                                        />
                                    ) : null}
                                    <span className="font-medium truncate">{calendar.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function CalendarCapacitiesSelectorUncontrolled({
    variant,
    options: externalOptions,
}: Pick<Props, "variant" | "options">) {
    const { selectedCalendars, toggleCalendar, toggleAllCalendars } = useCalendarCapacitiesSelection();
    const { options: loadedOptions } = useCalendarCapacitiesOptions();
    const options = externalOptions ?? loadedOptions;

    return (
        <CalendarCapacitiesSelectorContent
            options={options}
            selectedCalendars={selectedCalendars}
            toggleCalendar={toggleCalendar}
            toggleAllCalendars={toggleAllCalendars}
            variant={variant ?? CalendarCapacitiesSelectorVariant.LIST}
        />
    );
}

/**
 * Calendar checkboxes for the capacities page, dashboard widget, and remote config editor.
 */
export function CalendarCapacitiesSelector(props: Props = {}) {
    const { options: externalOptions, variant = CalendarCapacitiesSelectorVariant.LIST } = props;
    const { options: loadedOptions } = useCalendarCapacitiesOptions();
    const options = externalOptions ?? loadedOptions;

    if (!("onToggle" in props) || props.onToggle === undefined) {
        return <CalendarCapacitiesSelectorUncontrolled variant={variant} options={externalOptions} />;
    }

    return (
        <CalendarCapacitiesSelectorContent
            options={options}
            selectedCalendars={props.selectedCalendars}
            toggleCalendar={props.onToggle}
            toggleAllCalendars={props.onToggleAll}
            variant={variant}
        />
    );
}
